import type {
  Comment,
  CreateCommentPayload,
  CreateThreadPayload,
  DeleteCommentResult,
  Thread,
  ThreadListQuery,
  UpdateCommentPayload,
  UpdateThreadPayload,
} from '@atlas/api-contract';

import { apiClient } from './client';

/**
 * `/v1/versions/:versionId/threads`, `/v1/threads/:id`, `/v1/comments/:id`.
 *
 * Thin, like every other request module here — the axios interceptor has
 * already normalised failures into `ApiError` and React Query owns caching.
 *
 * Two asymmetries in this family are deliberate and are the reason the calls
 * are grouped in one file rather than split by noun:
 *
 * - **A thread is created with its first comment in a single request**
 *   (spec R5.1). There is no "create an empty thread": a pin with nothing said
 *   about it is not a review comment, and a two-request create could leave one
 *   behind if the second call failed.
 * - **`deleteComment` answers 200 with a body, not 204.** Deleting a thread's
 *   opening comment deletes the thread and every reply under it (spec R5.5),
 *   and the caller cannot tell which happened from the request alone. The
 *   `threadDeleted` flag in the response is what lets the viewer drop the pin
 *   without going back to the server for a thread that no longer exists.
 */

/** Request options every read accepts, so React Query can cancel it. */
interface ReadOptions {
  signal?: AbortSignal | undefined;
}

/**
 * `signal` is spread conditionally rather than passed as `undefined`: axios
 * declares it without `| undefined`, and `exactOptionalPropertyTypes` is on.
 */
const withSignal = ({ signal }: ReadOptions) =>
  signal === undefined ? {} : { signal };

/**
 * `GET /v1/versions/:versionId/threads` — every thread on one version, newest
 * first, each with its comments already nested oldest-first.
 *
 * Not paginated, and that is the right call rather than an oversight: the
 * canvas has to draw *every* pin at once, so a page would either be a lie about
 * how many pins exist or would need re-assembling client-side into the same
 * complete list.
 */
export const listThreads = async (
  versionId: string,
  query: ThreadListQuery,
  options: ReadOptions = {},
): Promise<Thread[]> => {
  const { data } = await apiClient.get<Thread[]>(
    `/versions/${encodeURIComponent(versionId)}/threads`,
    { params: query, ...withSignal(options) },
  );

  return data;
};

/**
 * `POST /v1/versions/:versionId/threads` — the anchor plus the first comment.
 *
 * The response is the finished thread with that comment already in `comments`,
 * so the new pin can be drawn from it without a refetch.
 */
export const createThread = async (
  versionId: string,
  payload: CreateThreadPayload,
): Promise<Thread> => {
  const { data } = await apiClient.post<Thread>(
    `/versions/${encodeURIComponent(versionId)}/threads`,
    payload,
  );

  return data;
};

/**
 * `PATCH /v1/threads/:id` — resolve or reopen (spec R5.6).
 *
 * Idempotent: two people resolving the same thread is a normal outcome, not a
 * conflict, and the recorded resolver is whoever's write landed last.
 */
export const setThreadResolved = async (
  threadId: string,
  payload: UpdateThreadPayload,
): Promise<Thread> => {
  const { data } = await apiClient.patch<Thread>(
    `/threads/${encodeURIComponent(threadId)}`,
    payload,
  );

  return data;
};

/** `POST /v1/threads/:id/comments` — a reply. 404 if the thread has gone. */
export const createComment = async (
  threadId: string,
  payload: CreateCommentPayload,
): Promise<Comment> => {
  const { data } = await apiClient.post<Comment>(
    `/threads/${encodeURIComponent(threadId)}/comments`,
    payload,
  );

  return data;
};

/** `PATCH /v1/comments/:id` — rewrites the body and stamps `editedAt`. */
export const updateComment = async (
  commentId: string,
  payload: UpdateCommentPayload,
): Promise<Comment> => {
  const { data } = await apiClient.patch<Comment>(
    `/comments/${encodeURIComponent(commentId)}`,
    payload,
  );

  return data;
};

/**
 * `DELETE /v1/comments/:id` — **200 with a body**, not 204.
 *
 * See the module comment: `threadDeleted` is the only way to know whether the
 * pin survived.
 */
export const deleteComment = async (
  commentId: string,
): Promise<DeleteCommentResult> => {
  const { data } = await apiClient.delete<DeleteCommentResult>(
    `/comments/${encodeURIComponent(commentId)}`,
  );

  return data;
};
